import { useAtomValue } from 'jotai';
import { request } from '@/api/apiUtils';
import { isSseJobComplete, SSE_JOB_TTL_MS } from '@/api/sse/listenSse';
import { runSseJob } from '@/api/sse/runSseJob';
import { tokenAtom } from '@/atoms/auth';
import { activeImportMarkerAtom, importInProgressAtom } from '@/atoms/import';
import { jotaiStore } from '@/atoms/store';
import { EDITOR_ENTITY_ROOTS } from '@/data/editorDataLayer/internal/editorEntityRoots';
import { IMPORT_REPORTS_ROOT } from '@/data/entities/importReports';
import { useCustomMutation } from '@/data/mutations';
import type { SseJobCallbacks, SseJobEvent } from '@/types';

// Query roots refetched when a document import finishes. An import rewrites the
// whole project, so everything the editor renders (EDITOR_ENTITY_ROOTS) is
// stale; it also produces a new import-report row, so that list refreshes too.
// Fired at SSE completion from both the live mutation and the resume listener.
export const IMPORT_COMPLETE_ROOTS = [...EDITOR_ENTITY_ROOTS, IMPORT_REPORTS_ROOT];

interface ImportFileParams extends SseJobCallbacks {
    project_id: number;
    file: File;
    actPrompt?: string;
    episodePrompt?: string;
    scenePrompt?: string;
    genericLogline?: boolean;
    genericSynopsis?: boolean;
    outlineOnly?: boolean;
}

// Hard cap on one import's lifetime; also the TTL of the persisted resume marker.
export const IMPORT_TTL_MS = SSE_JOB_TTL_MS;

// `ImportProjectJob::pushMessage` publishes exactly this text when it finishes.
// The backend SSE stream never closes on its own (blocking Redis subscribe) and
// has no structured terminal event, so this literal is the completion signal.
// Depending on the backend version it arrives as {"type":"event","status":"success"}
// or {"type":"progress","status":"100"} — match the text on either, and treat
// reaching 100% as terminal in its own right.
const IMPORT_DONE_MESSAGE = 'Project imported';

export const isImportFinished = (event: SseJobEvent) => event.message === IMPORT_DONE_MESSAGE || isSseJobComplete(event);

export const IMPORT_TIMEOUT_MESSAGE = 'Import timed out';

export function useImportFile() {
    const token = useAtomValue(tokenAtom);

    return useCustomMutation<ImportFileParams, unknown>({
        mutationFn: async ({
            project_id,
            file,
            actPrompt,
            episodePrompt,
            scenePrompt,
            genericLogline,
            genericSynopsis,
            outlineOnly,
            onSseEvent,
            onSseError,
            onSseComplete,
        }) => {
            const formData = new FormData();
            formData.append('document', file);
            const act = actPrompt?.trim();
            const episode = episodePrompt?.trim();
            const scene = scenePrompt?.trim();
            if (act) formData.append('act-prompt', act);
            if (episode) formData.append('episode-prompt', episode);
            if (scene) formData.append('scene-prompt', scene);
            // Laravel's `boolean` rule accepts "1"/"0"; send explicit values so the
            // toggle state is unambiguous rather than relying on omission.
            formData.append('generic-logline', genericLogline ? '1' : '0');
            formData.append('generic-synopsis', genericSynopsis ? '1' : '0');
            formData.append('outline-only', outlineOnly ? '1' : '0');

            // Lock the project while the backend rewrites its content. Set inside
            // mutationFn (after the MutationCache read-only gate has passed), so the
            // import mutation itself is not blocked by its own lock. The persisted
            // marker lets a page refresh re-attach to the stream.
            const startedAt = Date.now();
            jotaiStore.set(importInProgressAtom, true);
            jotaiStore.set(activeImportMarkerAtom, { project_id, started_at: startedAt });

            try {
                // The upload is `start`: the listener is already open when it
                // fires. A job that reports an error rejects here (SseJobError).
                return await runSseJob({
                    token,
                    projectId: project_id,
                    deadlineAt: startedAt + IMPORT_TTL_MS,
                    isFinished: isImportFinished,
                    timeoutMessage: IMPORT_TIMEOUT_MESSAGE,
                    onSseEvent,
                    onSseError,
                    onSseComplete,
                    start: () => request<unknown>(`projects/${project_id}/import`, { method: 'POST', body: formData }),
                });
            } finally {
                jotaiStore.set(importInProgressAtom, false);
                jotaiStore.set(activeImportMarkerAtom, null);
            }
        },
        invalidate: IMPORT_COMPLETE_ROOTS,
    });
}
